<template>
  <div>
    <v-container fluid grid-list-md>
      <v-layout justify-space-between>
        <v-flex xs3 sm3 md3 d-flex>
          <div>
            <permission-tooltip
                :permission="hasPermission('surveys_create')"
                :tooltip="{right: true, bottom: false, attach: false}"
            >
              <add-survey-modal
                  ref="addSurveyModal"
                  @survey_stored="fetchData"
                  @survey_closed="modalClosed()"
                  :currentSurvey="currentSurvey"
                  :disabled="!hasPermission('surveys_create')"
              />
            </permission-tooltip>
          </div>
        </v-flex>
      </v-layout>
      <v-layout>
        <v-flex xs12 sm3 md3 d-flex>
          <div>
            <v-text-field v-model="filters.search"
                          append-icon="search"
                          :label="trans('app.search.search')"
                          single-line
                          hide-details
                          solo
                          clearable
            ></v-text-field>
          </div>
        </v-flex>
        <v-flex xs12 sm3 md3 d-flex>
          <div>
            <v-combobox
                clearable
                :items="[{text: trans('app.own'), value: 'own'},{text: trans('app.shared'), value: 'shared'}]"
                :label="trans('app.database')"
                v-model="filters.database"
                hide-details
                solo
            ></v-combobox>
          </div>
        </v-flex>
      </v-layout>
    </v-container>
    <v-data-table
        :headers="headers"
        :items="items"
        :pagination.sync="pagination"
        :total-items="total"
        :loading="isLoading"
        :rows-per-page-items="[10,25,50,100]"
    >
      <v-progress-linear slot="progress" color="blue" indeterminate></v-progress-linear>
      <template slot="items" slot-scope="props">
        <tr>
          <td>
            {{ props.item.name }}
            <template v-if="props.item.shared">
              <v-tooltip right>
                <template #activator="{ on }">
                  <v-icon v-on="on" small color="success">share</v-icon>
                </template>
                <span>{{ trans('app.sharing.shared_by', {name: props.item.owned_by}) }}</span>
              </v-tooltip>
            </template>
          </td>
          <td class="text-xs-center">
            {{ props.item.created_at|format_date(FORMAT_DATETIME) }}
          </td>
          <td class="justify-end layout px-0 pr-4">
            <v-layout align-center justify-end>
              <icon-button
                  :permission="canEdit()"
                  icon="edit"
                  color="success"
                  cls="mr-0 ml-0"
                  :tooltip="{top: true, bottom: false, attach: false}"
                  :iconOptions="{small: true}"
                  @click="editSurvey(props.item)"
              >
                {{ 'app.show_and_edit' | trans }}
              </icon-button>
              <icon-button
                  @click="copy(props.item)"
                  icon="file_copy"
                  color="light-blue"
                  cls="mr-0 ml-0"
                  :permission="canCopy(props.item)"
                  :tooltip="{top: true, bottom: false, attach: false}"
                  :iconOptions="{small: true}"
              >
                {{ 'app.copy' | trans }}
              </icon-button>
              <icon-button
                  v-if="canDelete(props.item)"
                  :permission="canDelete(props.item)"
                  icon="delete"
                  color="red"
                  cls="mr-0 ml-0"
                  :tooltip="{top: true, bottom: false, attach: false}"
                  :iconOptions="{small: true}"
                  @click="deleteSurvey(props.item)"
              >
                {{ 'app.delete' | trans }}
              </icon-button>
            </v-layout>
          </td>
        </tr>
      </template>
      <template v-slot:no-data>
        <div class="text-center">
                <span v-if="(filters.database && filters.database.value === 'shared') && !canUseSharing" v-html="trans('app.no_access_tooltip')">
                </span>
          <span v-else>
                  {{ trans('app.search.no_data') }}
                </span>
        </div>
      </template>
    </v-data-table>
  </div>
</template>
<script>
import {trans} from "../../app";
import {stringify} from "qs";
import debounce from "lodash/debounce";
import goTo from 'vuetify/lib/components/Vuetify/goTo';
import {mapGetters} from "vuex"
import {FORMAT_DATETIME} from "../../utils/dateFormatter";

export default {
  data: function () {
    return {
      FORMAT_DATETIME: FORMAT_DATETIME,
      currentSurvey: null,
      headers: [
        {
          text: trans('app.name'), align: 'left', value: 'name', sortable: true,
        },
        {
          text: trans('app.createdAt'), align: 'center', value: 'created_at', sortable: true,
        },
        {
          text: '', align: 'center', sortable: false,
        },
      ],
      items: [],
      pagination: {
        rowsPerPage: 50,
        sortBy: null,
        descending: false,
      },
      total: 0,
      isLoading: true,
      filters: {
        search: null,
        database: null,
      }
    }
  },
  computed: {
    ...mapGetters(['hasPermission', 'canUseSharing']),
  },
  watch: {
    filters: {
      handler() {
        this.debounceFetch();
      },
      deep: true,
    },
    pagination: {
      handler(newValue, oldValue) {
        if (oldValue.page !== undefined) {
          this.fetchData();
        }
      },
      deep: true,
    },
  },
  created() {
    this.debounceFetch = debounce(this.fetchData, 300);
  },
  methods: {
    resetSearch() {
      this.filters = {
        search: null,
        database: null,
      };
      this.pagination.sortBy = null;
      this.pagination.descending = false;
    },
    async fetchData() {
      this.isLoading = true;
      const {sortBy, descending, page, rowsPerPage} = this.pagination;

      const query = {
        page: page,
        perPage: rowsPerPage,
        orderBy: sortBy,
        dir: descending ? 'desc' : 'asc',
        ...this.filters,
      };

      if (this.filters.database) {
        query.database = this.filters.database.value;
      }

      const response = await axios.get(`/v2/surveys?${stringify(query, {skipNulls: true})}`);
      const {data, meta} = response.data;
      this.items = data;
      this.total = meta.total;
      this.pages = meta.last_page;
      this.isLoading = false;
    },
    editSurvey: function (survey) {
      this.currentSurvey = survey;
      this.$refs.addSurveyModal.open();
    },
    modalClosed() {
      this.currentSurvey = null;
    },
    async copy(item) {
      await axios.put(`/v2/surveys/${item.id}/copy`);
      await this.fetchData();
      this.$toast(trans('app.actions.copied', {name: trans('app.survey_accusative')}));
    },
    deleteSurvey(item) {
      this.$confirmDanger(
          trans('app.surveys.confirm_survey_removal', {name: item.name}),
          trans('app.surveys.remove_survey')
      ).then(async () => {
        goTo(0, {
          duration: 1000
        });
        await axios.delete(`/v2/surveys/${item.id}`);
        await this.fetchData();
        this.$toast(trans('app.actions.removed', {name: trans('app.survey_accusative')}));
      }).catch((error) => {
        this.sentryCaptureException(error);
      })
    },
    canEdit() {
      return this.hasPermission('surveys_edit');
    },
    canCopy() {
      return this.hasPermission('surveys_copy');
    },
    canDelete(item) {
      return !item.shared && this.hasPermission('surveys_delete');
    }
  }
}
</script>
